// import dotenv from 'dotenv';
// dotenv.config();

import { useLoadingOverlay } from "./../components/LoadingOverlayProvider";
import axios from "axios";
import { useEffect, useState } from "react";
import {
  MenuFetch,
  Profile,
  IndividualView,
  IndividualProfile,
  CompanyView,
  CompanyProfile,
  IndividualLookupData,
  ToastColor,
  ToastPlacement,
  LawEnforcementLookupData,
  Balance,
  AccountStatus,
} from "./types";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Converts IndividualView to IndividualProfile
 */
export function individualViewToProfile(
  view: IndividualView
): IndividualProfile {
  return {
    userId: view.id,
    firstName: view.firstName,
    surname: view.surname,
    middleName: view.middleName,
    nationalIdNumber: view.nationalIdNumber,
    cellPhone: view.cellPhone,
    gender: view.gender,
    email: view.email,
    mobileMoneyNumber: view.mobileMoneyNumber,
    nationality:
      typeof view.nationality === "object" && view.nationality
        ? view.nationality.value
        : view.nationality,
    townCityId: view.townCityId,
    houseNumberStreet: view.houseNumberStreet,
    suburb: view.suburb,
  };
}

/**
 * Converts CompanyView to CompanyProfile
 */
export function companyViewToProfile(view: CompanyView): CompanyProfile {
  return {
    userId: view.id,
    companyName: view.companyName,
    tradingName: view.tradingName,
    registrationNumber: view.registrationNumber,
    townCityId: view.townCityId,
    physicalAddress: view.physicalAddress,
    primaryContactFirstName: view.primaryContactFirstName,
    primaryContactSurname: view.primaryContactSurname,
    primaryContactIdNumber: view.primaryContactIdNumber,
    primaryPassportNumber: view.primaryPassportNumber,
    primaryContactRole: view.primaryContactRole,
    primaryContactNumber: view.primaryContactNumber,
    primaryContactEmail: view.primaryContactEmail,
    billingContactFirstName: view.billingContactFirstName,
    billingContactSurname: view.billingContactSurname,
    billingContactIdNumber: view.billingContactIdNumber,
    billingContactPassportNumber: view.billingContactPassportNumber,
    billingContactEmail: view.billingContactEmail,
    billingContactNumber: view.billingContactNumber,
    primaryContactIdentityDocumentId: view.primaryContactIdentityDocumentId,
    certificateIncorpFileId: view.certificateIncorpFileId,
    cr14FileId: view.cr14FileId,
    companyType: view.companyType,
  };
}

export const API = localStorage.getItem("assethost");

export const passwordRegex =
  // eslint-disable-next-line no-useless-escape
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;

export const passwordError =
  "Password must be at least 8 characters, with uppercase, lowercase, number, and symbol.";

export const usernameRegex = /^(?!\s*$)[-_a-zA-Z0-9 ]*$/;

export const usernameError =
  "Username should only contain letters, digits, dash, or underscore";

export const emailRegex =
  /^[\w.!#$%&'*+/=?^`{|}~-]+@[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?(?:\.[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?)+$/i;

export const emailError =
  "Please enter a valid email address (e.g. name@example.com)";

export const nationalIdRegex = /^\d{2}-\d{6,7}[A-Z]\d{2}$/;

export const nationalIdError =
  "Accepted format for national ID is 00-000000(0)A00";

export const phoneRegex =
  /^\+?[0-9]{1,4}?[-.\s]?\(?[0-9]{1,3}?\)?[-.\s]?[0-9]{3,4}[-.\s]?[0-9]{3,4}$/;

export const phoneError = "Not a valid phone number";

export const dynamicNationalIdError = (id: string): string => {
  const start = "Accepted format for national ID is ";
  let last = "";
  const ogPlaceholder = "00-000000(0)A00";
  const placeholder1 = "00-000000A00";
  const placeholder2 = "00-0000000A00";
  if (id.length >= 10 && /[A-Z]/.test(id[9])) {
    last = id + placeholder1.substring(id.length);
  } else if (id.length >= 10) {
    last = id + placeholder2.substring(id.length);
  } else {
    last = id + ogPlaceholder.substring(id.length);
  }
  return start + last;
};

export const primaryLabelName = (assetType: string): string => {
  switch (assetType) {
    case "MOBILE":
      return "Brand";
    case "ELECTRONICS":
      return "Brand";
    case "ELECTRICAL":
      return "Brand";
    case "MECHANICAL":
      return "Brand";
    case "FURNITURE":
      return "Brand";
    case "VEHICLES":
      return "Make";
    case "ANIMALS":
      return "Animal Type";
    default:
      return "Brand";
  }
};

export const secondaryLabelName = (assetType: string): string => {
  switch (assetType) {
    case "MOBILE":
      return "Model";
    case "ELECTRONICS":
      return "Model";
    case "ELECTRICAL":
      return "Model";
    case "MECHANICAL":
      return "Model";
    case "FURNITURE":
      return "Model";
    case "VEHICLES":
      return "Model";
    case "ANIMALS":
      return "Breed";
    default:
      return "Model";
  }
};

export const uniqueIdentifier1Name = (assetType: string): string => {
  switch (assetType) {
    case "MOBILE":
      return "IMEI 1";
    case "ELECTRONICS":
      return "Serial Number";
    case "ELECTRICAL":
      return "Serial Number";
    case "MECHANICAL":
      return "Tag Number";
    case "FURNITURE":
      return "Serial Number";
    case "VEHICLES":
      return "Registration Number";
    case "ANIMALS":
      return "Tag Number";
    default:
      return "Serial Number";
  }
};

export const uniqueIdentifier2Name = (assetType: string): string => {
  switch (assetType) {
    case "MOBILE":
      return "IMEI 2";
    case "ELECTRONICS":
      return "";
    case "ELECTRICAL":
      return "";
    case "MECHANICAL":
      return "";
    case "FURNITURE":
      return "";
    case "VEHICLES":
      return "Chasis Number";
    case "ANIMALS":
      return "";
    default:
      return "";
  }
};

export function useMenus() {
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loadingIncrement, loadingDecrement, showToast } = useLoadingOverlay();
  const [menus, setMenus] = useState<MenuFetch[]>([
    {
      name: "Main",
      menuViews: [
        {
          name: "Assets",
          menuFunction: "/assets",
        },
        {
          name: "Check",
          menuFunction: "/check",
        },
        {
          name: "Flagged Items",
          menuFunction: "/flagged",
        },
        {
          name: "Reports",
          menuFunction: "/reports",
        },
      ],
    },
  ]);

  useEffect(() => {
    if (sessionStorage.getItem("menus")) {
      setMenus(JSON.parse(sessionStorage.getItem("menus")!));
      return;
    }
    const token = sessionStorage.getItem("token");
    const roleId = sessionStorage.getItem("roleId");
    if (!(token && roleId)) {
      // showToast({
      //   color: ToastColor.Danger,
      //   closeButton: true,
      //   title:
      //     "Login details not properly recorded, please return to login screen",
      //   placement: ToastPlacement.TopRight,
      //   timeout: 3000,
      // });
      loadingDecrement();
      window.location.href = `/?redirectUrl=${
        window.location.pathname + window.location.search
      }`;
      return;
    }
    loadingIncrement();
    axios
      .get(API + `/settings/getusermenus/${roleId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setMenus(response.data);
        sessionStorage.setItem("menus", JSON.stringify(response.data));
      })
      .catch((error) => {
        console.log(error);
        if (Number(error.status) === 403) {
          sessionStorage.clear();
          navigate(`/?redirectUrl=${location.pathname + location.search}`);
        }
      })
      .finally(() => {
        loadingDecrement();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return menus;
}

export function useProfile(id?: number) {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<Profile>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loadingIncrement, loadingDecrement, showToast } = useLoadingOverlay();

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const userId = id ? id : sessionStorage.getItem("userId");
    if (!(token && userId)) {
      // showToast({
      //   color: ToastColor.Danger,
      //   closeButton: true,
      //   title:
      //     "Login details not properly recorded, please return to login screen",
      //   placement: ToastPlacement.TopRight,
      //   timeout: 3000,
      // });
      loadingDecrement();
      window.location.href = `/?redirectUrl=${
        window.location.pathname + window.location.search
      }`;
      return;
    }
    loadingIncrement();
    axios
      .get(API + `/users/${userId}/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setProfile(response.data);
      })
      .catch((error) => {
        console.log(error);
        if (Number(error.status) === 403) {
          navigate(`/?redirectUrl=${location.pathname + location.search}`);
        }
      })
      .finally(() => {
        loadingDecrement();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return profile;
}

export function useDropdownLists() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const location = useLocation();
  const [individualLookupData, setIndividualLookupData] =
    useState<IndividualLookupData>({} as IndividualLookupData);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loadingIncrement, loadingDecrement, showToast } = useLoadingOverlay();

  useEffect(() => {
    loadingIncrement();
    axios
      .get(API + "/register/getindividualdropdownlists")
      .then((response) => {
        setIndividualLookupData(response.data);
      })
      .catch((error) => {
        // showToast({
        //   color: ToastColor.Danger,
        //   closeButton: true,
        //   title:
        //     error?.response?.data?.message ||
        //     "Failed to get lookup data. Refresh page or contact administrator.",
        //   placement: ToastPlacement.TopRight,
        //   timeout: 3000,
        //   timed: true,
        // });
      })
      .finally(() => {
        loadingDecrement();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return individualLookupData;
}

export function useLawDropdownLists() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const location = useLocation();
  const [lawEnforcementLookupData, setLawEnforcementLookupData] =
    useState<LawEnforcementLookupData>({} as LawEnforcementLookupData);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loadingIncrement, loadingDecrement, showToast } = useLoadingOverlay();

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      // showToast({
      //   color: ToastColor.Danger,
      //   closeButton: true,
      //   title:
      //     "Login details not properly recorded, please return to login screen",
      //   placement: ToastPlacement.TopRight,
      //   timeout: 3000,
      // });
      loadingDecrement();
      window.location.href = `/?redirectUrl=${
        window.location.pathname + window.location.search
      }`;
      return;
    }

    loadingIncrement();
    axios
      .get(API + "/law-enforcement/register/dropdownlists", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setLawEnforcementLookupData(response.data);
      })
      .catch((error) => {
        // showToast({
        //   color: ToastColor.Danger,
        //   closeButton: true,
        //   title:
        //     error?.response?.data?.message ||
        //     "Failed to get lookup data. Refresh page or contact administrator.",
        //   placement: ToastPlacement.TopRight,
        //   timeout: 3000,
        //   timed: true,
        // });
      })
      .finally(() => {
        loadingDecrement();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return lawEnforcementLookupData;
}

export function useMobileMoneyNames(): string[] {
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mobileMoneyNames, setMobileMoneyNames] = useState<string[]>(
    [] as string[]
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loadingIncrement, loadingDecrement, showToast } = useLoadingOverlay();

  useEffect(() => {
    loadingIncrement();
    const token = sessionStorage.getItem("token");
    if (!token) {
      // showToast({
      //   color: ToastColor.Danger,
      //   closeButton: true,
      //   title:
      //     "Login details not properly recorded, please return to login screen",
      //   placement: ToastPlacement.TopRight,
      //   timeout: 3000,
      // });
      loadingDecrement();
      window.location.href = `/?redirectUrl=${
        window.location.pathname + window.location.search
      }`;
      return;
    }
    axios
      .get(API + "/settings/getmobilemoneynames", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setMobileMoneyNames(response.data);
      })
      .catch((error) => {
        // showToast({
        //   color: ToastColor.Danger,
        //   closeButton: true,
        //   title:
        //     error?.response?.data?.message ||
        //     "Failed to get lookup data. Refresh page or contact administrator.",
        //   placement: ToastPlacement.TopRight,
        //   timeout: 3000,
        //   timed: false,
        // });
        console.log(error);
        if (Number(error.status) === 403) {
          navigate(`/?redirectUrl=${location.pathname + location.search}`);
        }
      })
      .finally(() => {
        loadingDecrement();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return mobileMoneyNames;
}

export function useBalance(time: Date): Balance {
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [balance, setBalance] = useState<Balance>({} as Balance);
  const { loadingIncrement, loadingDecrement, showToast } = useLoadingOverlay();

  useEffect(() => {
    loadingIncrement();
    const token = sessionStorage.getItem("token");
    const individualProfileId = window.sessionStorage.getItem(
      "individualProfileId"
    );
    if (!token) {
      // showToast({
      //   color: ToastColor.Danger,
      //   closeButton: true,
      //   title:
      //     "Login details not properly recorded, please return to login screen",
      //   placement: ToastPlacement.TopRight,
      //   timeout: 3000,
      // });
      loadingDecrement();
      window.location.href = `/?redirectUrl=${
        window.location.pathname + window.location.search
      }`;
      return;
    }
    if (!individualProfileId) {
      // showToast({
      //   color: ToastColor.Danger,
      //   closeButton: true,
      //   title: "Logged in account is not an individual account",
      //   placement: ToastPlacement.TopRight,
      //   timeout: 3000,
      // });
      loadingDecrement();
      return;
    }
    axios
      .get(API + `/billing/getbalance/${individualProfileId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setBalance(response.data);
      })
      .catch((error) => {
        showToast({
          color: ToastColor.Danger,
          closeButton: true,
          title: error?.response?.data?.message || "Failed to get balance.",
          placement: ToastPlacement.TopRight,
          timeout: 3000,
          timed: false,
        });
        console.log(error);
        if (Number(error.status) === 403) {
          navigate(`/?redirectUrl=${location.pathname + location.search}`);
        }
      })
      .finally(() => {
        loadingDecrement();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  return balance;
}

export function useAccountStatus(time: Date): AccountStatus {
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [status, setStatus] = useState<AccountStatus>({} as AccountStatus);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loadingIncrement, loadingDecrement, showToast } = useLoadingOverlay();

  useEffect(() => {
    loadingIncrement();
    const token = sessionStorage.getItem("token");
    const companyProfileId = window.sessionStorage.getItem("companyProfileId");
    if (!token) {
      // showToast({
      //   color: ToastColor.Danger,
      //   closeButton: true,
      //   title:
      //     "Login details not properly recorded, please return to login screen",
      //   placement: ToastPlacement.TopRight,
      //   timeout: 3000,
      // });
      loadingDecrement();
      window.location.href = `/?redirectUrl=${
        window.location.pathname + window.location.search
      }`;
      return;
    }
    if (!companyProfileId) {
      // showToast({
      //   color: ToastColor.Danger,
      //   closeButton: true,
      //   title: "Logged in account is not a company account",
      //   placement: ToastPlacement.TopRight,
      //   timeout: 3000,
      // });
      loadingDecrement();
      return;
    }
    axios
      .get(API + `/billing/getaccountstatus/${companyProfileId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setStatus(response.data);
      })
      .catch((error) => {
        // showToast({
        //   color: ToastColor.Danger,
        //   closeButton: true,
        //   title:
        //     error?.response?.data?.message || "Failed to get account status.",
        //   placement: ToastPlacement.TopRight,
        //   timeout: 3000,
        //   timed: false,
        // });
        console.log(error);
        if (Number(error.status) === 403) {
          navigate(`/?redirectUrl=${location.pathname + location.search}`);
        }
      })
      .finally(() => {
        loadingDecrement();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  return status;
}

function getNestedValue<T extends Record<string, any>>(
  obj: T,
  path: string
): any {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

function setNestedValue<T extends Record<string, any>>(
  obj: T,
  path: string,
  value: any
): T {
  const keys = path.split(".");
  const lastKey = keys.pop()!;
  const newObj: any = { ...obj };
  let curr = newObj;

  for (const k of keys) {
    curr[k] = { ...curr[k] };
    curr = curr[k];
  }
  curr[lastKey] = value;
  return newObj;
}

const ogPlaceholder = "00-000000(0)A00";
const placeholder1 = "00-000000A00";
const placeholder2 = "00-0000000A00";

export type NationalIdResult<T> = {
  newPlaceholder: string;
  error: string;
  updatedObject: T;
};

/**
 * Validates and formats a Zimbabwean national ID input.
 *
 * @param e - The raw input string
 * @param currentObject - The current object holding identification field
 * @param field - The name of the identification field (defaults to "identification")
 * @param isZimbabwean - Whether validation rules should apply
 */
export function formatNationalId<T extends Record<string, any>>(
  e: string,
  currentObject: T,
  field: string = "identification",
  isZimbabwean = true
): NationalIdResult<T> {
  let currentValue = String(getNestedValue(currentObject, field) ?? "");
  let newValue = currentValue;
  let newPlaceholder =
    currentValue + ogPlaceholder.substring(currentValue.length);
  let error = "";
  e = e.toUpperCase();

  if (!isZimbabwean) {
    const updatedObject = setNestedValue(currentObject, field, e);
    return { newPlaceholder, error, updatedObject };
  }

  if (e.length > currentValue.length && nationalIdRegex.test(currentValue)) {
    return {
      newPlaceholder,
      error,
      updatedObject: currentObject,
    };
  }

  if (nationalIdRegex.test(e)) {
    newValue = e;
    newPlaceholder = e;
  } else if (
    nationalIdRegex.test(e + placeholder1.substring(e.length)) ||
    nationalIdRegex.test(e + placeholder2.substring(e.length))
  ) {
    if (e.length >= 10) {
      if (/[A-Z]/.test(e[9])) {
        newValue = e;
        newPlaceholder = e + placeholder1.substring(e.length);
      } else {
        newValue = e;
        newPlaceholder = e + placeholder2.substring(e.length);
      }
    } else if (e.length === 2 && currentValue.length < 2) {
      newValue = e + "-";
      newPlaceholder = newValue + ogPlaceholder.substring(newValue.length);
    } else {
      newValue = e;
      newPlaceholder = e + ogPlaceholder.substring(e.length);
    }
  } else {
    error = dynamicNationalIdError(currentValue);
  }

  const updatedObject = setNestedValue(currentObject, field, newValue);

  return { newPlaceholder, error, updatedObject };
}
